import React, { useState } from 'react';
import { BrainPocketArticle } from '../../utils/BrainPocket/brainPocketStorage';

type EditorProps = {
  addArticle: (input: { urlInput: string; titleInput: string }) => Promise<void>;
  selectedArticle: BrainPocketArticle | null;
  toggleRead: (articleId: string) => void;
  addKeyPoint: (articleId: string, point: string) => void;
  deleteKeyPoint: (articleId: string, index: number) => void;
  deleteArticle: (articleId: string) => void;
  totals: {
    domains: number;
    articles: number;
    keyPoints: number;
    unread: number;
  };
};

export default function Editor({
  addArticle,
  selectedArticle,
  toggleRead,
  addKeyPoint,
  deleteKeyPoint,
  deleteArticle,
  totals,
}: EditorProps) {
  const [urlInput, setUrlInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [point, setPoint] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isSavingArticle, setIsSavingArticle] = useState(false);

  async function handleAddArticle(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    try {
      if (!urlInput.trim()) {
        setUrlError('Paste a URL first.');
        return;
      }

      setIsSavingArticle(true);
      await addArticle({ urlInput, titleInput });
      setUrlInput('');
      setTitleInput('');
      setUrlError(null);
    } catch {
      setUrlError('That URL could not be parsed.');
    } finally {
      setIsSavingArticle(false);
    }
  }

  function handleAddKeyPoint(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedArticle) {
      return;
    }

    addKeyPoint(selectedArticle.id, point);
    setPoint('');
  }

  return (
    <div className="brainpocket-editor">
      <section className="brainpocket-capture" aria-label="Save an article">
        <form className="brainpocket-capture-form" onSubmit={handleAddArticle}>
          <label>
            <span>Article URL</span>
            <input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/article"
              disabled={isSavingArticle}
            />
          </label>
          <label>
            <span>Title</span>
            <input
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              placeholder="Optional title"
              disabled={isSavingArticle}
            />
          </label>
          <button
            type="submit"
            className="brainpocket-button brainpocket-button-primary"
            disabled={isSavingArticle}
          >
            {isSavingArticle ? 'Scraping & analyzing article...' : 'Save Idea'}
          </button>
        </form>
        {urlError && <p className="brainpocket-form-error">{urlError}</p>}
      </section>

      <section className="brainpocket-detail">
        {!selectedArticle ? (
          <div className="brainpocket-empty-state">
            <h2>Build your local knowledge tree.</h2>
            <p>
              Save an article URL and BrainPocket will create a domain node, place the article
              beneath it, and let you attach key points as child nodes.
            </p>
            <dl>
              <div>
                <dt>{totals.domains}</dt>
                <dd>domains</dd>
              </div>
              <div>
                <dt>{totals.articles}</dt>
                <dd>articles</dd>
              </div>
              <div>
                <dt>{totals.keyPoints}</dt>
                <dd>key points</dd>
              </div>
            </dl>
          </div>
        ) : (
          <article className="brainpocket-article-detail">
            <header className="brainpocket-article-header">
              <div>
                <p className="brainpocket-article-status">
                  {selectedArticle.read ? 'Read' : 'Unread'} · Saved {selectedArticle.savedAt}
                </p>
                <h2>{selectedArticle.title}</h2>
                <a
                  href={selectedArticle.url}
                  target="_blank"
                  rel="noreferrer"
                  className="brainpocket-url"
                >
                  {selectedArticle.url}
                </a>
              </div>
              <div className="brainpocket-detail-actions">
                <button
                  type="button"
                  onClick={() => toggleRead(selectedArticle.id)}
                  className="brainpocket-button brainpocket-button-secondary"
                >
                  {selectedArticle.read ? 'Mark Unread' : 'Mark Read'}
                </button>
                <button
                  type="button"
                  onClick={() => deleteArticle(selectedArticle.id)}
                  className="brainpocket-button brainpocket-button-danger"
                >
                  Delete
                </button>
              </div>
            </header>

            <section className="brainpocket-keypoints">
              <div className="brainpocket-section-header">
                <h3>Key Points</h3>
                <span>{selectedArticle.keyPoints.length}</span>
              </div>
              {selectedArticle.keyPoints.length === 0 ? (
                <p className="brainpocket-empty-note">Add the first key point for this article.</p>
              ) : (
                <ul>
                  {selectedArticle.keyPoints.map((kp, index) => (
                    <li key={`${kp}-${index}`}>
                      <span>{kp}</span>
                      <button
                        type="button"
                        onClick={() => deleteKeyPoint(selectedArticle.id, index)}
                        aria-label="Delete key point"
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form className="brainpocket-keypoint-form" onSubmit={handleAddKeyPoint}>
                <input
                  value={point}
                  onChange={(e) => setPoint(e.target.value)}
                  placeholder="Add a key point"
                />
                <button type="submit" className="brainpocket-button brainpocket-button-secondary">
                  Add
                </button>
              </form>
            </section>
          </article>
        )}
      </section>
    </div>
  );
}
